// Gap kept between the field's bottom edge and the keyboard's top edge.
export const FIELD_KEYBOARD_GAP = 16;

// Nearest scrollable ancestor.
export function getScrollParent(node: HTMLElement | null): HTMLElement | null {
  for (let el = node?.parentElement ?? null; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
  }
  return null;
}

// The scroller's direct child on the field's path. Its height drives scrollHeight (the page content
// is absolutely positioned, so padding the scroller itself adds no room) — so we pad this instead.
export function getScrollContent(field: HTMLElement, scrollParent: HTMLElement | null): HTMLElement | null {
  let el = field;
  while (el.parentElement && el.parentElement !== scrollParent) el = el.parentElement;
  return scrollParent && el.parentElement === scrollParent ? el : null;
}

// Bottom space missing to lift the field above the keyboard, beyond what current scroll allows.
function getMissingScrollSpace(field: HTMLElement, scrollParent: HTMLElement, keyboardTop: number): number {
  const needToScroll = field.getBoundingClientRect().bottom - (keyboardTop - FIELD_KEYBOARD_GAP);
  const available = scrollParent.scrollHeight - scrollParent.scrollTop - scrollParent.clientHeight;
  return Math.ceil(needToScroll - available);
}

function addBottomPadding(el: HTMLElement, amount: number): void {
  const base = parseFloat(getComputedStyle(el).paddingBottom) || 0;
  el.style.paddingBottom = `${base + amount}px`;
}

/**
 * One lift pass: reserve the keyboard's height under the field, manufacture the missing scroll room,
 * and scroll the field clear of a keyboard whose top edge sits at `keyboardTop`. `basePadding` is the
 * content's padding before any lift, so re-runs don't stack their own padding into the measurement.
 */
export function scrollFieldAboveKeyboard(
  field: HTMLElement,
  keyboardTop: number,
  scrollParent: HTMLElement | null,
  content: HTMLElement | null,
  basePadding: string
): void {
  const blocked = Math.max(0, window.innerHeight - keyboardTop);
  field.style.scrollMarginBottom = `${Math.ceil(blocked) + FIELD_KEYBOARD_GAP}px`;
  if (scrollParent && content) {
    content.style.paddingBottom = basePadding;
    const missingSpace = getMissingScrollSpace(field, scrollParent, keyboardTop);
    if (missingSpace > 0) addBottomPadding(content, missingSpace);
  }
  field.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}
